import { useRouter } from "@core/Api/useRouter";
import FetchService from "@core-ui/ApiServices/FetchService";
import ApiUrlCreatorService from "@core-ui/ContextServices/ApiUrlCreator";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import { cn } from "@core-ui/utils/cn";
import { relocateToUrl } from "@ext/enterprise/components/SingInOut/hooks/useSignIn";
import completeGesCloudDesktopLogin from "@ext/enterprise-cloud/desktop/completeGesCloudDesktopLogin";
import { GesCloudApi, type GesCloudEmailCodeRejection } from "@ext/enterprise-cloud/GesCloudApi";
import t from "@ext/localization/locale/translate";
import { Button } from "@ui-kit/Button";
import { Icon } from "@ui-kit/Icon";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "@ui-kit/InputOTP";
import { useState } from "react";
import { type EmailCodeStatus, GesCloudEmailCodeStatus } from "./GesCloudEmailCodeStatus";
import { useCountdown } from "./useCountdown";

type VerifiedCode = GesCloudEmailCodeRejection | { resultType: "verified"; completeLogin: () => Promise<void> };

const toRejectedStatus = (rejection: GesCloudEmailCodeRejection): EmailCodeStatus =>
	rejection.reason === "invalidCode"
		? { name: "invalidCode", attemptsLeft: rejection.attemptsLeft }
		: { name: "codeExpired" };

interface GesCloudEmailCodeVerifyProps {
	gesCloudUrl: string;
	email: string;
	codeLength: number;
	retryAfterSeconds: number;
	onBack: () => void;
}

export const GesCloudEmailCodeVerify = ({
	gesCloudUrl,
	email,
	codeLength,
	retryAfterSeconds,
	onBack,
}: GesCloudEmailCodeVerifyProps) => {
	const apiUrlCreator = ApiUrlCreatorService.value;
	const router = useRouter();
	const { environment } = usePlatform();
	const [code, setCode] = useState("");
	const [status, setStatus] = useState<EmailCodeStatus>({ name: "idle" });
	const [isResending, setIsResending] = useState(false);
	const [cooldownSeconds, setCooldownSeconds] = useCountdown(retryAfterSeconds);

	const verifyOnDesktop = async (value: string): Promise<VerifiedCode> => {
		const result = await new GesCloudApi(gesCloudUrl).verifyEmailCode({ email, code: value, client: "desktop" });
		if (result.resultType === "rejected") return result;
		return {
			resultType: "verified",
			completeLogin: () => completeGesCloudDesktopLogin(result.oneTimeCode, apiUrlCreator, router, gesCloudUrl),
		};
	};

	const verifyOnWeb = async (value: string): Promise<VerifiedCode> => {
		const result = await new GesCloudApi(gesCloudUrl).verifyEmailCode({
			email,
			code: value,
			client: "web",
			from: window.location.href,
		});
		if (result.resultType === "rejected") return result;
		return { resultType: "verified", completeLogin: async () => relocateToUrl(result.redirectUrl) };
	};

	const verifyCode = async (value: string) => {
		setStatus({ name: "verifying" });

		let verified: VerifiedCode;
		try {
			verified = environment === "tauri" ? await verifyOnDesktop(value) : await verifyOnWeb(value);
		} catch {
			setCode("");
			setStatus({ name: "verifyFailed" });
			return;
		}

		if (verified.resultType === "rejected") {
			setCode("");
			setStatus(toRejectedStatus(verified));
			return;
		}

		try {
			await FetchService.fetch(apiUrlCreator.getEnableCloudUrl());
			await verified.completeLogin();
		} catch {
			setStatus({ name: "loginNotCompleted" });
		}
	};

	const resendCode = async () => {
		setIsResending(true);
		try {
			const result = await new GesCloudApi(gesCloudUrl).sendEmailCode(email);
			if (result.resultType === "sent") {
				setCode("");
				setStatus({ name: "idle" });
				setCooldownSeconds(result.retryAfterSeconds);
			} else if (result.reason === "invalidEmail") setStatus({ name: "resendFailed" });
			else setCooldownSeconds(result.retryAfterSeconds);
		} catch {
			setStatus({ name: "resendFailed" });
		} finally {
			setIsResending(false);
		}
	};

	const onCodeChange = (value: string) => {
		setCode(value);
		setStatus({ name: "idle" });
		if (value.length === codeLength) void verifyCode(value);
	};

	const isVerifying = status.name === "verifying";
	const isInvalid = status.name === "invalidCode";
	const slotClassName = cn(isInvalid && "border-status-error-primary-border");
	const firstGroupLength = Math.ceil(codeLength / 2);
	const renderSlots = (from: number, to: number) =>
		Array.from({ length: to - from }, (_, offset) => from + offset).map((index) => (
			<InputOTPSlot className={slotClassName} index={index} key={index} />
		));

	return (
		<div className="flex flex-col items-center gap-4 text-center">
			<Icon className="text-muted" icon="mail" size="xl" />
			<div className="flex flex-col items-center gap-1">
				<h2 className="text-lg font-semibold">{t("enterprise-cloud-guest.email-code.checkEmail")}</h2>
				<p className="text-sm text-muted">
					{t("enterprise-cloud-guest.email-code.codeSentTo").replace("{length}", String(codeLength))}{" "}
					<span className="font-medium text-primary-fg">{email}</span>
				</p>
				<Button disabled={isVerifying} onClick={onBack} type="button" variant="text">
					{t("enterprise-cloud-guest.email-code.changeEmail")}
				</Button>
			</div>
			<InputOTP
				aria-label={t("enterprise-cloud-guest.email-code.codeLabel")}
				autoComplete="one-time-code"
				autoFocus
				data-qa="ges-cloud-email-code-code"
				disabled={isVerifying || status.name === "loginNotCompleted"}
				maxLength={codeLength}
				onChange={onCodeChange}
				pasteTransformer={(pasted) => pasted.replace(/\D/g, "")}
				value={code}
			>
				<InputOTPGroup>{renderSlots(0, firstGroupLength)}</InputOTPGroup>
				<InputOTPSeparator />
				<InputOTPGroup>{renderSlots(firstGroupLength, codeLength)}</InputOTPGroup>
			</InputOTP>
			<GesCloudEmailCodeStatus
				cooldownSeconds={cooldownSeconds}
				isResending={isResending}
				onResend={resendCode}
				onStartOver={onBack}
				status={status}
			/>
		</div>
	);
};
